import { motion } from "framer-motion";
import { QrCode, ShoppingBag, CreditCard, ShoppingBasket, Truck, Sparkles, Languages, Bell, Utensils, Pizza, Coffee, Wine, ArrowRight, Clock, ShieldCheck, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reveal } from "@/components/landing/motion";

const proof = [
  { big: "5 min", small: "per pubblicare il primo menù" },
  { big: "0 €", small: "di ristampe: aggiorni e basta" },
  { big: "14 giorni", small: "di prova gratis sui piani a pagamento" },
  { big: "100%", small: "incassi diretti sul tuo conto" },
];

export const ProofStrip = () => (
  <section className="py-14 border-y border-gray-100 bg-[#FAFBFA]" data-testid="proof-strip">
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
      {proof.map((p, i) => (
        <motion.div key={p.big} {...reveal(i * 0.08)} data-testid={`proof-${i}`}>
          <div className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight text-gray-900">{p.big}</div>
          <p className="text-gray-500 text-sm mt-1">{p.small}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const pillars = [
  { icon: QrCode, title: "Menù digitale", tag: "Da Free", text: "Un QR e un link per tutti i tavoli e i social. Foto, allergeni, menù del giorno: cambi un prezzo e i clienti lo vedono subito." },
  { icon: ShoppingBag, title: "Ordini dal tavolo", tag: "Pro e Business", text: "Il cliente sceglie dal telefono e l'ordine arriva alla tua dashboard con notifica. Meno attese, più coperti serviti." },
  { icon: CreditCard, title: "Paga il conto", tag: "Pro e Business", text: "Conto pagato con carta, Apple Pay o Google Pay direttamente dal tavolo. I soldi arrivano sul tuo conto Stripe, non passano da noi." },
  { icon: ShoppingBasket, title: "Asporto", tag: "Business", text: "Prenotazioni da asporto con fasce orarie di ritiro che decidi tu. Niente più telefonate durante il servizio." },
  { icon: Truck, title: "FoodTruck", tag: "Da Basic", text: "Dì ai clienti dove sei oggi e a che ora. Programma la settimana e crea un menù dedicato per ogni serata." },
  { icon: Sparkles, title: "Menù con l'AI", tag: "Incluso", text: "Incolla il tuo menù di carta: l'intelligenza artificiale crea categorie, piatti e allergeni in pochi secondi." },
];

export const Pillars = () => (
  <section id="funzioni" className="py-28 bg-gray-950 text-white relative overflow-hidden">
    <div className="absolute -top-40 left-1/3 h-96 w-96 rounded-full bg-[#5aa469]/25 blur-[120px] pointer-events-none max-w-full" />
    <div className="relative max-w-7xl mx-auto px-6">
      <motion.div {...reveal()} className="max-w-3xl mb-16">
        <p className="text-[#8fd39c] text-xs tracking-[0.25em] uppercase mb-4">Cosa fa MenuOne</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">Dal QR al pagamento, tutto il servizio in un'unica app</h2>
        <p className="text-gray-400 text-lg mt-5 leading-relaxed">Parti dal menù gratis. Quando sei pronto, accendi ordini, pagamenti e asporto senza cambiare strumento.</p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pillars.map((f, i) => (
          <motion.div key={f.title} {...reveal(i * 0.07)}
            className="group rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-8 flex flex-col gap-6 hover:-translate-y-1 hover:border-[#5aa469]/60 hover:bg-white/[0.07] transition-[transform,border-color,background-color]" data-testid={`pillar-${i}`}>
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-xl bg-[#5aa469]/15 border border-[#5aa469]/30 flex items-center justify-center group-hover:bg-[#5aa469] transition-colors">
                <f.icon className="h-5 w-5 text-[#8fd39c] group-hover:text-white transition-colors" />
              </div>
              <span className="text-[11px] uppercase tracking-wide text-gray-400 border border-white/10 rounded-full px-2.5 py-1">{f.tag}</span>
            </div>
            <div>
              <h3 className="font-display font-semibold text-2xl mb-2">{f.title}</h3>
              <p className="text-gray-400 leading-relaxed text-sm">{f.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const extras = [
  { icon: Languages, title: "Multilingua", text: "Il cliente straniero legge il menù nella sua lingua con un tocco." },
  { icon: Bell, title: "Lista d'attesa", text: "I clienti si mettono in coda dal telefono e li avvisi quando il tavolo è pronto." },
  { icon: Clock, title: "Menù del giorno", text: "Il piatto di oggi in evidenza, senza toccare il resto del menù." },
  { icon: ShieldCheck, title: "Allergeni e diete", text: "Filtri per allergeni, vegetariano, vegano e senza glutine, sempre in regola." },
];

export const Extras = () => (
  <section className="py-24" data-testid="extras">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
      <motion.div {...reveal()}>
        <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Dettagli che contano</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight">Piccole cose che i clienti notano subito</h2>
      </motion.div>
      <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
        {extras.map((e, i) => (
          <motion.div key={e.title} {...reveal(i * 0.08)} className="flex gap-4" data-testid={`extra-${i}`}>
            <div className="h-10 w-10 rounded-xl bg-[#5aa469]/10 flex items-center justify-center shrink-0"><e.icon className="h-5 w-5 text-[#4a8a56]" /></div>
            <div>
              <h3 className="font-display font-semibold text-lg">{e.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mt-1">{e.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const audiences = [
  { icon: Utensils, title: "Ristoranti e trattorie", text: "Ordini e conto dal tavolo: il personale serve, non fa il cassiere." },
  { icon: Pizza, title: "Pizzerie e asporto", text: "Prenotazioni da asporto con orario di ritiro, senza telefono che squilla." },
  { icon: Coffee, title: "Bar e caffetterie", text: "Menù gratis con QR e menù del giorno, pronto in 5 minuti." },
  { icon: Truck, title: "FoodTruck e sagre", text: "Tappe della settimana e menù dedicato per ogni serata." },
  { icon: Wine, title: "Eventi e locali serali", text: "Lista d'attesa e menù multilingua per turisti e serate affollate." },
];

export const Audiences = ({ goStart }) => (
  <section className="py-24 bg-[#FAFBFA] border-y border-gray-100" data-testid="audiences">
    <div className="max-w-7xl mx-auto px-6">
      <motion.div {...reveal()} className="max-w-2xl mb-12">
        <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Per chi è</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Pensato per ogni tipo di locale</h2>
      </motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {audiences.map((a, i) => (
          <motion.button key={a.title} {...reveal(i * 0.06)} onClick={goStart} className="text-left rounded-[1.5rem] bg-white border border-gray-200 p-6 hover:border-[#5aa469]/60 hover:-translate-y-1 transition-[transform,border-color] group" data-testid={`audience-${i}`}>
            <a.icon className="h-6 w-6 text-[#5aa469] mb-4" />
            <h3 className="font-display font-semibold text-lg leading-tight">{a.title}</h3>
            <p className="text-gray-600 text-sm mt-2 leading-relaxed">{a.text}</p>
            <span className="inline-flex items-center gap-1 text-[#4a8a56] text-sm font-medium mt-4 group-hover:gap-2 transition-all">Inizia <ArrowRight className="h-3.5 w-3.5" /></span>
          </motion.button>
        ))}
      </div>
    </div>
  </section>
);

const faqs = [
  { q: "Quanto ci vuole per partire?", a: "Registrazione, menù e QR in circa 5 minuti. Se hai il menù di carta, incollalo e l'AI lo trasforma in menù digitale." },
  { q: "Serve un'app per i clienti?", a: "No. Il cliente inquadra il QR con la fotocamera e il menù si apre nel browser. Niente da scaricare." },
  { q: "Come arrivano i soldi dei pagamenti al tavolo?", a: "Direttamente sul tuo conto Stripe, collegato in due minuti dalle Impostazioni. MenuOne non tocca i tuoi incassi." },
  { q: "Posso cambiare piano o smettere?", a: "Sì, quando vuoi dalla sezione Abbonamento. Il piano Free resta gratis per sempre." },
  { q: "I prezzi sono IVA inclusa?", a: "Sì, tutti i prezzi indicati sono IVA inclusa. Ricevi fattura per ogni pagamento." },
];

export const Faq = () => (
  <section id="faq" className="py-24" data-testid="faq">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.5fr] gap-12">
      <motion.div {...reveal()}>
        <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Domande frequenti</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Le risposte che cerchi</h2>
        <div className="mt-8 flex items-center gap-3 text-gray-600 text-sm"><Headphones className="h-5 w-5 text-[#5aa469]" /> Assistenza in italiano: <a href="mailto:creamenuone@gmail.com" className="text-[#4a8a56] font-medium hover:underline">creamenuone@gmail.com</a></div>
      </motion.div>
      <div className="divide-y divide-gray-200">
        {faqs.map((f, i) => (
          <motion.details key={f.q} {...reveal(i * 0.05)} className="group py-5" data-testid={`faq-${i}`}>
            <summary className="flex items-center justify-between cursor-pointer list-none font-display font-semibold text-lg text-gray-900">
              {f.q}<span className="ml-4 h-7 w-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="text-gray-600 leading-relaxed mt-3 pr-10">{f.a}</p>
          </motion.details>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCta = ({ goStart }) => (
  <section className="py-24 px-6">
    <motion.div {...reveal()} className="max-w-7xl mx-auto rounded-[2.5rem] bg-[#5aa469] text-white px-8 py-20 relative overflow-hidden">
      <div className="absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-white/15 blur-3xl" />
      <div className="absolute -top-32 -left-20 h-80 w-80 rounded-full bg-black/10 blur-3xl" />
      <div className="relative max-w-3xl">
        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-5">Il tuo menù online stasera. Gratis.</h2>
        <p className="text-white/85 text-lg mb-9 max-w-xl">Nessuna carta richiesta per il piano Free. Passa a ordini e pagamenti solo quando ti serve, con 14 giorni di prova: poi resti sul Free o continui col piano scelto.</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={goStart} size="lg" className="rounded-full bg-white hover:bg-gray-100 text-gray-900 font-semibold px-9 h-12 active:scale-95 transition-transform" data-testid="final-cta">Crea il menù gratis</Button>
          <a href="#prezzi"><Button size="lg" variant="outline" className="rounded-full border-white/50 bg-transparent text-white hover:bg-white/10 px-8 h-12" data-testid="final-pricing">Confronta i piani</Button></a>
        </div>
      </div>
    </motion.div>
  </section>
);
